import { GraphQLQuery, generateClient } from 'aws-amplify/api';
import React, { ReactNode, createContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ListOrganizationUsersFilteredQuery,
  OrganizationGetByTenantQuery,
  OrganizationUserStatus
} from '@/API';
import { organizationGetByTenant, userCountByOrganization } from '@/graphql/queries';
import { listOrganizationUsersFilteredLite as listOrganizationUsersFiltered } from '@/graphql/queries.custom';
import { OrgType } from '../Organizations/orgs.model';
import { OrgUserType } from './OrgUserType.model';
import config from '@/config';
import { useAuth } from '@/providers/AuthProvider';

type OrgUserContextType = {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  isEdit: boolean;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  editFocus: string;
  setEditFocus: React.Dispatch<React.SetStateAction<string>>;
  currentOrgUser: OrgUserType;
  setCurrentUser: React.Dispatch<React.SetStateAction<OrgUserType>>;
  currentOrgUsers: any[];
  setCurrentOrgUsers: React.Dispatch<React.SetStateAction<any[]>>;
  usersNextToken?: string;
  setUsersNextToken: React.Dispatch<React.SetStateAction<string | undefined>>;

  currentOrganization: OrgType;
  setCurrentOrganization: React.Dispatch<React.SetStateAction<OrgType>>;
  gqlQueryOrgs: boolean;
  setGqlQueryOrgs: React.Dispatch<React.SetStateAction<boolean>>;
};

type OrgUserContextProviderProp = {
  children: ReactNode;
};

const OrgUserContext = createContext<OrgUserContextType>({} as OrgUserContextType);

const OrgUserContextProvider = ({ children }: OrgUserContextProviderProp) => {
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editFocus, setEditFocus] = useState('');
  const [currentOrgUser, setCurrentUser] = useState<OrgUserType>({
    id: '',
    organizationID: '',
    username: '',
    email: '',
    createdAt: '',
    role: '',
    accountStatus: '',
    groupsCanRead: []
  });

  const [currentOrgUsers, setCurrentOrgUsers] = useState<any[]>([]);
  const [usersNextToken, setUsersNextToken] = useState<string | undefined>(undefined);

  const [gqlQueryOrgs, setGqlQueryOrgs] = useState(true);
  const [currentOrganization, setCurrentOrganization] = useState<OrgType>({
    id: '',
    image: '',
    name: '',
    createdAt: '',
    numAdmin: 0,
    numHost: 0,
    status: '',
    activeUsers: 0,
    activeHosts: 0,
    activeAdmins: 0,
    emailDomains: [],
    url: '',
    tenantId: '',
    groupsCanRead: []
  });

  const client = generateClient();
  const { tenant } = useAuth();
  const { tenantId: paramTenant } = useParams();
  const tenantId = paramTenant ?? tenant;

  useEffect(() => {
    const fetchData = async () => {
      if (tenantId === undefined) {
        return;
      }

      try {
        // First query: Get organization by tenant
        const orgResponse = await client.graphql<GraphQLQuery<OrganizationGetByTenantQuery>>({
          query: organizationGetByTenant,
          variables: {
            tenant: tenantId
          }
        });

        const tempCurrentOrg = orgResponse.data.organizationGetByTenant;
        if (!tempCurrentOrg) {
          throw new Error('Organization not found');
        }

        // Fetch user count without loading all users
        const countResp = await client.graphql({
          query: userCountByOrganization,
          variables: { organizationId: tempCurrentOrg.id }
        });

        const activeUsersCount = countResp.data.userCountByOrganization ?? {
          activeHosts: 0,
          activeAdmins: 0
        };

        setCurrentOrganization({
          id: tempCurrentOrg.id,
          name: tempCurrentOrg.name,
          createdAt: tempCurrentOrg.createdAt,
          numAdmin: tempCurrentOrg.adminLicenses,
          numHost: tempCurrentOrg.hostLicenses,
          status: tempCurrentOrg.organizationActive?.toString(),
          url: `${tempCurrentOrg.tenant}.${config.domain}`,
          emailDomains: tempCurrentOrg.emailDomains,
          activeHosts: activeUsersCount.hostCount,
          activeAdmins: activeUsersCount.adminCount,
          activeUsers: activeUsersCount.hostCount + activeUsersCount.adminCount,
          tenantId: tempCurrentOrg.tenant,
          groupsCanRead: tempCurrentOrg.groupsCanRead
        } as OrgType);

        if (location.pathname.endsWith('/users') || location.pathname.endsWith('/invite')) {
          const usersResponse = await client.graphql<
            GraphQLQuery<ListOrganizationUsersFilteredQuery>
          >({
            query: listOrganizationUsersFiltered,
            variables: {
              filter: {
                organizationId: {
                  eq: tempCurrentOrg.id
                },
                accountStatus: {
                  ne: OrganizationUserStatus.DELETED
                }
              },
              limit: 10
            }
          });

          const conn = usersResponse.data.listOrganizationUsersFiltered as NonNullable<
            ListOrganizationUsersFilteredQuery['listOrganizationUsersFiltered']
          >;

          setCurrentOrgUsers((conn?.items ?? []).filter(Boolean) as any[]);
          setUsersNextToken(conn?.nextToken ?? undefined);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Handle error appropriately
      }
    };

    fetchData();
    setGqlQueryOrgs(false);
  }, [gqlQueryOrgs, tenantId, location.pathname]);

  return (
    <OrgUserContext.Provider
      value={{
        showModal,
        setShowModal,
        isEdit,
        setIsEdit,
        editFocus,
        setEditFocus,
        currentOrgUser,
        setCurrentUser,
        currentOrgUsers,
        setCurrentOrgUsers,
        usersNextToken,
        setUsersNextToken,
        currentOrganization,
        setCurrentOrganization,
        gqlQueryOrgs,
        setGqlQueryOrgs
      }}
    >
      {children}
    </OrgUserContext.Provider>
  );
};

export { OrgUserContext, OrgUserContextProvider };
